import {
  Alert,
  Breadcrumbs,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Form } from "react-bootstrap";
import AddIcon from "@mui/icons-material/Add";
import ViewListIcon from "@mui/icons-material/ViewList";
import ViewModuleIcon from "@mui/icons-material/ViewModule";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import BlockIcon from "@mui/icons-material/Block";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DirectionsCarFilledOutlinedIcon from "@mui/icons-material/DirectionsCarFilledOutlined";
import VehiclePageLoader from "../../Components/Loaders/VehiclePageLoader";
import { instance } from "../../Const/ApiHeader";
import {
  ALL_VEHICLE,
  DELETE_VEHICLE,
  DUPLICATE_VEHICLE,
  resolveImageUrl,
  SET_VEHICLE_STATUS,
} from "../../Const/ApiConst";

const DISCONTINUED = "Discontinued";
const ACTIVE = "Active";
const PAGE_SIZE = 24;

const isDiscontinued = (status) =>
  status === "Discontinued" || status === "Discountinued";

function VehicleActions({ vehicle, busyId, onDuplicate, onToggleStatus, onDelete }) {
  const discontinued = isDiscontinued(vehicle.status);
  const disabled = busyId === vehicle._id;

  return (
    <div className="vehicle-card-actions" onClick={(e) => e.preventDefault()}>
      <Tooltip title="Edit vehicle">
        <IconButton
          size="small"
          component={Link}
          to={`/Update_Vehicle/${vehicle._id}`}
          onClick={(e) => e.stopPropagation()}
        >
          <EditOutlinedIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Tooltip title="Duplicate vehicle">
        <IconButton
          size="small"
          disabled={disabled}
          onClick={(e) => onDuplicate(e, vehicle._id)}
        >
          <ContentCopyIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Tooltip title={discontinued ? "Mark as active" : "Mark as discontinued"}>
        <IconButton
          size="small"
          color={discontinued ? "success" : "warning"}
          disabled={disabled}
          onClick={(e) => onToggleStatus(e, vehicle)}
        >
          {discontinued ? (
            <CheckCircleOutlineIcon fontSize="small" />
          ) : (
            <BlockIcon fontSize="small" />
          )}
        </IconButton>
      </Tooltip>
      <Tooltip title="Delete vehicle">
        <IconButton
          size="small"
          color="error"
          disabled={disabled}
          onClick={(e) => onDelete(e, vehicle)}
        >
          <DeleteOutlineIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    </div>
  );
}

function VehicleGridCard({ vehicle, busyId, onDuplicate, onToggleStatus, onDelete }) {
  const discontinued = isDiscontinued(vehicle.status);
  const isBusy = busyId === vehicle._id;

  return (
    <article
      className={`vehicle-card ${discontinued ? "is-discontinued" : ""} ${
        isBusy ? "is-busy" : ""
      }`}
    >
      {isBusy ? (
        <div className="vehicle-card-busy-overlay" aria-label="Updating vehicle">
          <CircularProgress size={28} />
        </div>
      ) : null}
      <Link to={`/Update_Vehicle/${vehicle._id}`} className="vehicle-card-link">
        <div className="vehicle-card-media">
          {vehicle.vehicleImage ? (
            <img src={resolveImageUrl(vehicle.vehicleImage)} alt="" className="vehicle-card-image" />
          ) : (
            <div className="vehicle-card-image-fallback">
              <DirectionsCarFilledOutlinedIcon />
            </div>
          )}
          <Chip
            size="small"
            label={discontinued ? "Discontinued" : "Active"}
            color={discontinued ? "default" : "success"}
            className="vehicle-card-status"
          />
        </div>
        <div className="vehicle-card-body">
          <Typography className="vehicle-card-brand">
            {vehicle.manufacturerType || "Unknown brand"}
          </Typography>
          <Typography className="vehicle-card-name">
            {vehicle.vehicle_name || "Unnamed vehicle"}
          </Typography>
          <div className="vehicle-card-meta">
            <span>{vehicle.vehicleCategory || "Uncategorized"}</span>
            {vehicle.engineCC ? <span>{vehicle.engineCC} cc</span> : null}
          </div>
        </div>
      </Link>
      <VehicleActions
        vehicle={vehicle}
        busyId={busyId}
        onDuplicate={onDuplicate}
        onToggleStatus={onToggleStatus}
        onDelete={onDelete}
      />
    </article>
  );
}

function Vehicles() {
  const [viewType, setViewType] = useState("grid");
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [vehicleList, setVehicleList] = useState([]);
  const [busyId, setBusyId] = useState(null);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [manufacturerFilter, setManufacturerFilter] = useState("all");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [hasMore, setHasMore] = useState(true);
  const [filteredTotal, setFilteredTotal] = useState(0);
  const [stats, setStats] = useState({ total: 0, active: 0, discontinued: 0 });
  const [manufacturerOptions, setManufacturerOptions] = useState([]);
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteError, setDeleteError] = useState("");
  const sentinelRef = useRef(null);
  const pageRef = useRef(1);
  const loadingMoreRef = useRef(false);
  const requestIdRef = useRef(0);

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput.trim());
    }, 350);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const applyPayloadMeta = (payload, nextList) => {
    const pagination = payload.pagination;
    setFilteredTotal(
      pagination?.total ?? nextList.length
    );
    setHasMore(Boolean(pagination?.hasNextPage));
    if (payload.stats) {
      setStats(payload.stats);
    } else {
      const discontinued = nextList.filter((v) => isDiscontinued(v.status)).length;
      setStats({
        total: nextList.length,
        active: nextList.length - discontinued,
        discontinued,
      });
    }
    if (Array.isArray(payload.manufacturers)) {
      setManufacturerOptions(payload.manufacturers);
    }
    if (Array.isArray(payload.categories)) {
      setCategoryOptions(payload.categories);
    }
  };

  const loadPage = useCallback(
    async (pageToLoad, { append = false } = {}) => {
      if (append) {
        if (loadingMoreRef.current) return;
        loadingMoreRef.current = true;
        setLoadingMore(true);
      } else {
        setLoading(true);
        setHasMore(true);
      }

      const requestId = ++requestIdRef.current;

      try {
        const response = await instance.get(ALL_VEHICLE, {
          params: {
            page: pageToLoad,
            limit: PAGE_SIZE,
            search,
            status: statusFilter,
            manufacturer: manufacturerFilter,
            category: categoryFilter,
          },
        });
        if (requestId !== requestIdRef.current) return;

        const payload = response?.data || {};
        const nextList = payload.data || [];

        setVehicleList((prev) => {
          if (!append) return nextList;
          const seen = new Set(prev.map((vehicle) => vehicle._id));
          return [...prev, ...nextList.filter((vehicle) => !seen.has(vehicle._id))];
        });
        pageRef.current = pageToLoad;
        applyPayloadMeta(payload, nextList);

        if (!Array.isArray(payload.manufacturers)) {
          setManufacturerOptions((prev) => {
            if (prev.length) return prev;
            return [
              ...new Set(nextList.map((v) => v.manufacturerType).filter(Boolean)),
            ].sort();
          });
        }
        if (!Array.isArray(payload.categories)) {
          setCategoryOptions((prev) => {
            if (prev.length) return prev;
            return [
              ...new Set(nextList.map((v) => v.vehicleCategory).filter(Boolean)),
            ].sort();
          });
        }
      } catch (err) {
        console.log(err);
        if (requestId !== requestIdRef.current) return;
        if (!append) {
          setVehicleList([]);
        }
        setHasMore(false);
      } finally {
        if (append) {
          loadingMoreRef.current = false;
        }
        if (requestId === requestIdRef.current) {
          setLoading(false);
          setLoadingMore(false);
        }
      }
    },
    [search, statusFilter, manufacturerFilter, categoryFilter]
  );

  useEffect(() => {
    pageRef.current = 1;
    setVehicleList([]);
    loadPage(1, { append: false });
  }, [loadPage]);

  useEffect(() => {
    const node = sentinelRef.current;
    if (!node || loading || loadingMore || !hasMore || vehicleList.length === 0) {
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          loadPage(pageRef.current + 1, { append: true });
        }
      },
      { root: node.closest("main"), rootMargin: "600px" }
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [loading, loadingMore, hasMore, vehicleList.length, loadPage]);

  const handleDuplicate = async (e, id) => {
    e.preventDefault();
    e.stopPropagation();
    setBusyId(id);
    try {
      const response = await instance.post(DUPLICATE_VEHICLE, { id });
      const newVehicle = response.data?.data;
      if (newVehicle) {
        setVehicleList((prev) => {
          const index = prev.findIndex((vehicle) => vehicle._id === id);
          if (index === -1) return [newVehicle, ...prev];
          const next = [...prev];
          next.splice(index + 1, 0, newVehicle);
          return next;
        });
        const copyDiscontinued = isDiscontinued(newVehicle.status);
        setStats((prev) => ({
          total: prev.total + 1,
          active: copyDiscontinued ? prev.active : prev.active + 1,
          discontinued: copyDiscontinued
            ? prev.discontinued + 1
            : prev.discontinued,
        }));
        setFilteredTotal((prev) => prev + 1);
      }
    } catch (err) {
      console.log(err);
    } finally {
      setBusyId(null);
    }
  };

  const openDeleteDialog = (e, vehicle) => {
    e.preventDefault();
    e.stopPropagation();
    setDeleteError("");
    setDeleteTarget(vehicle);
  };

  const closeDeleteDialog = () => {
    if (!deleteLoading) {
      setDeleteTarget(null);
      setDeleteError("");
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    const id = deleteTarget._id;
    setBusyId(id);
    setDeleteLoading(true);
    setDeleteError("");
    try {
      await instance.post(DELETE_VEHICLE, { id });
      const wasDiscontinued = isDiscontinued(deleteTarget.status);
      setVehicleList((prev) => prev.filter((vehicle) => vehicle._id !== id));
      setStats((prev) => ({
        total: Math.max(0, prev.total - 1),
        active: wasDiscontinued ? prev.active : Math.max(0, prev.active - 1),
        discontinued: wasDiscontinued
          ? Math.max(0, prev.discontinued - 1)
          : prev.discontinued,
      }));
      setFilteredTotal((prev) => Math.max(0, prev - 1));
      setDeleteTarget(null);
    } catch (err) {
      console.log(err);
      setDeleteError("Failed to delete vehicle. Please try again.");
    } finally {
      setDeleteLoading(false);
      setBusyId(null);
    }
  };

  const handleToggleStatus = async (e, res) => {
    e.preventDefault();
    e.stopPropagation();
    const nextStatus = isDiscontinued(res.status) ? ACTIVE : DISCONTINUED;
    setBusyId(res._id);
    try {
      await instance.post(SET_VEHICLE_STATUS, {
        id: res._id,
        status: nextStatus,
      });
      setVehicleList((prev) =>
        prev.map((vehicle) =>
          vehicle._id === res._id ? { ...vehicle, status: nextStatus } : vehicle
        )
      );
      const nowDiscontinued = isDiscontinued(nextStatus);
      setStats((prev) => ({
        ...prev,
        active: nowDiscontinued
          ? Math.max(0, prev.active - 1)
          : prev.active + 1,
        discontinued: nowDiscontinued
          ? prev.discontinued + 1
          : Math.max(0, prev.discontinued - 1),
      }));
    } catch (err) {
      console.log(err);
    } finally {
      setBusyId(null);
    }
  };

  const resetFilters = (updater) => (event) => {
    updater(event.target.value);
  };

  return (
    <div className="vehicles-page">
      <Breadcrumbs aria-label="breadcrumb" className="mb-3">
        <Link to="/">Home</Link>
        <Typography>Vehicles</Typography>
      </Breadcrumbs>

      <section className="vehicles-hero">
        <div>
          <Typography className="vehicles-hero-eyebrow">
            <DirectionsCarFilledOutlinedIcon fontSize="inherit" /> Catalogue
          </Typography>
          <Typography variant="h4" className="vehicles-hero-title">
            Vehicles
          </Typography>
          <Typography className="vehicles-hero-subtitle">
            Browse, filter, and manage every model in your catalogue.
          </Typography>
        </div>
        <Link to="/add_vehicles">
          <Button variant="contained" startIcon={<AddIcon />}>
            Add Vehicle
          </Button>
        </Link>
      </section>

      <section className="vehicles-stats-grid">
        <div className="vehicles-stat-card">
          <Typography className="vehicles-stat-value">{stats.total}</Typography>
          <Typography className="vehicles-stat-label">Total vehicles</Typography>
        </div>
        <div className="vehicles-stat-card tone-green">
          <Typography className="vehicles-stat-value">{stats.active}</Typography>
          <Typography className="vehicles-stat-label">Active listings</Typography>
        </div>
        <div className="vehicles-stat-card tone-muted">
          <Typography className="vehicles-stat-value">{stats.discontinued}</Typography>
          <Typography className="vehicles-stat-label">Discontinued</Typography>
        </div>
        <div className="vehicles-stat-card tone-blue">
          <Typography className="vehicles-stat-value">
            {filteredTotal > 0
              ? `${vehicleList.length}/${filteredTotal}`
              : vehicleList.length}
          </Typography>
          <Typography className="vehicles-stat-label">Loaded</Typography>
        </div>
      </section>

      <section className="section vehicles-toolbar">
        <div className="vehicles-toolbar-filters">
          <Form.Control
            className="admin-search"
            type="search"
            placeholder="Search by brand, model, category..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
          />
          <Form.Select
            value={statusFilter}
            onChange={resetFilters(setStatusFilter)}
          >
            <option value="all">All statuses</option>
            <option value="active">Active</option>
            <option value="discontinued">Discontinued</option>
          </Form.Select>
          <Form.Select
            value={manufacturerFilter}
            onChange={resetFilters(setManufacturerFilter)}
          >
            <option value="all">All manufacturers</option>
            {manufacturerOptions.map((name) => (
              <option key={name} value={name}>
                {name}
              </option>
            ))}
          </Form.Select>
          <Form.Select
            value={categoryFilter}
            onChange={resetFilters(setCategoryFilter)}
          >
            <option value="all">All categories</option>
            {categoryOptions.map((name) => (
              <option key={name} value={name}>
                {name}
              </option>
            ))}
          </Form.Select>
        </div>
        <ToggleButtonGroup
          size="small"
          exclusive
          value={viewType}
          onChange={(_, value) => value && setViewType(value)}
          className="vehicles-view-toggle"
        >
          <ToggleButton value="grid" aria-label="Grid view">
            <ViewModuleIcon fontSize="small" />
          </ToggleButton>
          <ToggleButton value="list" aria-label="List view">
            <ViewListIcon fontSize="small" />
          </ToggleButton>
        </ToggleButtonGroup>
      </section>

      <section className="vehicles-results">
        {loading ? (
          <div
            className={
              viewType === "grid" ? "vehicles-grid" : "vehicles-list-shell"
            }
          >
            <VehiclePageLoader viewType={viewType} full />
          </div>
        ) : vehicleList.length === 0 ? (
          <div className="section vehicles-empty">
            <DirectionsCarFilledOutlinedIcon className="vehicles-empty-icon" />
            <Typography variant="h6">
              {stats.total === 0
                ? "No vehicles yet"
                : "No vehicles match your filters"}
            </Typography>
            <Typography color="text.secondary" className="mb-3">
              {stats.total === 0
                ? "Add your first vehicle to start building the catalogue."
                : "Try adjusting search or filter options."}
            </Typography>
            {stats.total === 0 ? (
              <Link to="/add_vehicles">
                <Button variant="contained" startIcon={<AddIcon />}>
                  Add Vehicle
                </Button>
              </Link>
            ) : null}
          </div>
        ) : viewType === "grid" ? (
          <div className="vehicles-grid">
            {vehicleList.map((vehicle) => (
              <VehicleGridCard
                key={vehicle._id}
                vehicle={vehicle}
                busyId={busyId}
                onDuplicate={handleDuplicate}
                onToggleStatus={handleToggleStatus}
                onDelete={openDeleteDialog}
              />
            ))}
          </div>
        ) : (
          <div className="section vehicles-list-shell">
            <table className="table admin-table vehicles-table mb-0">
              <thead>
                <tr>
                  <th>Vehicle</th>
                  <th>Category</th>
                  <th>Engine</th>
                  <th>Status</th>
                  <th className="text-end">Actions</th>
                </tr>
              </thead>
              <tbody>
                {vehicleList.map((vehicle) => {
                  const discontinued = isDiscontinued(vehicle.status);
                  const isBusy = busyId === vehicle._id;
                  return (
                    <tr
                      key={vehicle._id}
                      className={`${discontinued ? "is-discontinued" : ""} ${
                        isBusy ? "is-busy" : ""
                      }`}
                    >
                      <td>
                        <Link
                          to={`/Update_Vehicle/${vehicle._id}`}
                          className="vehicles-table-vehicle"
                        >
                          {vehicle.vehicleImage ? (
                            <img
                              src={resolveImageUrl(vehicle.vehicleImage)}
                              alt=""
                              className="vehicles-table-thumb"
                            />
                          ) : (
                            <div className="vehicles-table-thumb-fallback">
                              <DirectionsCarFilledOutlinedIcon fontSize="small" />
                            </div>
                          )}
                          <div>
                            <Typography className="vehicles-table-brand">
                              {vehicle.manufacturerType}
                            </Typography>
                            <Typography className="vehicles-table-name">
                              {vehicle.vehicle_name}
                            </Typography>
                          </div>
                        </Link>
                      </td>
                      <td>{vehicle.vehicleCategory || "-"}</td>
                      <td>{vehicle.engineCC ? `${vehicle.engineCC} cc` : "-"}</td>
                      <td>
                        <Chip
                          size="small"
                          label={discontinued ? "Discontinued" : "Active"}
                          color={discontinued ? "default" : "success"}
                        />
                      </td>
                      <td className="text-end">
                        {isBusy ? (
                          <div className="vehicles-table-busy">
                            <CircularProgress size={22} />
                          </div>
                        ) : (
                          <VehicleActions
                            vehicle={vehicle}
                            busyId={busyId}
                            onDuplicate={handleDuplicate}
                            onToggleStatus={handleToggleStatus}
                            onDelete={openDeleteDialog}
                          />
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {vehicleList.length > 0 ? (
          <div className="vehicles-infinite-footer" ref={sentinelRef}>
            {loadingMore ? (
              <CircularProgress size={22} />
            ) : hasMore ? (
              <Typography color="text.secondary">
                {vehicleList.length} of {filteredTotal}
              </Typography>
            ) : (
              <Typography color="text.secondary">
                {vehicleList.length} of {filteredTotal} vehicles
              </Typography>
            )}
          </div>
        ) : null}
      </section>

      <Dialog
        open={Boolean(deleteTarget)}
        onClose={closeDeleteDialog}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Delete vehicle?</DialogTitle>
        <DialogContent>
          {deleteError ? (
            <Alert severity="error" sx={{ mb: 2 }}>
              {deleteError}
            </Alert>
          ) : null}
          <DialogContentText>
            Are you sure you want to permanently delete{" "}
            <strong>
              {deleteTarget?.manufacturerType} {deleteTarget?.vehicle_name}
            </strong>
            ? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={closeDeleteDialog} disabled={deleteLoading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDelete}
            disabled={deleteLoading}
          >
            {deleteLoading ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}

export default Vehicles;
